import { Input } from '@shopflo/ui'
import React, { ChangeEvent, ChangeEventHandler, useRef, useEffect, useState } from 'react'
import FormErrorText from './FormErrorText'

interface InputFieldProps {
  label?: string
  labelClassName?: string
  maxSize?: number
  value: string | number | null
  onChange: (value: string) => void
  placeholder?: string
  prefix?: string
  isDisabled?: boolean
  variant?: 'outline' | 'filled'
  type?: React.HTMLInputTypeAttribute
  error?: string
  id?: string
  autoFocus?: boolean
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  labelClassName,
  maxSize,
  value,
  onChange,
  placeholder = '',
  prefix,
  isDisabled = false,
  variant = 'outline',
  type = 'text',
  error,
  id,
  autoFocus = false
}) => {
  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    onChange(event.target.value)
  }

  const [prefixWidth, setPrefixWidth] = useState(0)
  const prefixRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (prefixRef.current && prefix) {
      setPrefixWidth(prefixRef.current.offsetWidth)
    }
  }, [prefix])

  const displayValue = value === null ? '' : value

  return (
    <div className='flex flex-col w-full px-0.5'>
      <div className={`flex items-center justify-between mb-1 ${isDisabled ? 'opacity-40' : ''}`}>
        {label && <label className={labelClassName ?? 'text-text-xs-medium text-zinc-500'}>{label}</label>}
        {maxSize && typeof displayValue === 'string' && (
          <div className='text-xs text-gray-dark'>
            {displayValue.length}/{maxSize}
          </div>
        )}
      </div>
      <div className='relative w-full '>
        {prefix && (
          <div className='absolute inset-y-0 left-0 flex items-center pl-3'>
            <span ref={prefixRef} className='text-sm text-gray-500'>
              {prefix}
            </span>
          </div>
        )}
        <Input
          id={id}
          type={type}
          variant={variant}
          maxLength={maxSize}
          value={displayValue}
          dimension='md'
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled}
          autoFocus={autoFocus}
          style={prefix ? { paddingLeft: `calc(${prefixWidth}px + 1rem)` } : undefined}
          error={!!error}
        />
      </div>
      <FormErrorText message={error} />
    </div>
  )
}

export default InputField
